import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CreditCard, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentPage() {
  const { paperId } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/pay/${paperId}`).then(({ data }) => setInv(data)).catch(() => setInv(false)).finally(() => setLoading(false));
  }, [paperId]);

  if (loading) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!inv) return <div className="py-32 text-center text-slate-500" data-testid="payment-not-found">Invoice not found.</div>;

  const cleared = ["paid", "waived"].includes(inv.payment_status);
  const billing = "editorial@noventrapress.org";
  const Row = ({ k, v }) => (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-100 dark:border-slate-800 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="font-medium text-slate-800 dark:text-slate-200 text-right">{v}</span>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <div className="bg-card rounded-2xl border border-slate-200 dark:border-slate-800 p-8 shadow-sm" data-testid="payment-invoice">
        <div className="flex items-center gap-2 text-primary dark:text-sky-400 font-semibold"><CreditCard className="w-5 h-5" /> Article Processing Charge</div>
        <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mt-3">{inv.journal?.title || "Noventra Academic Press"}</h1>
        <p className="font-mono text-xs text-amber-600 mt-1">{inv.submission_id}</p>

        <div className="mt-6">
          <Row k="Manuscript" v={inv.title} />
          <Row k="Authors" v={(inv.authors || []).map((a) => a.name).join(", ")} />
          <Row k="Article Processing Charge" v={<span className="text-lg font-bold text-slate-900 dark:text-slate-100">{inv.fee}</span>} />
          <Row k="Status" v={<span className={`capitalize font-semibold ${cleared ? "text-emerald-600" : "text-amber-600"}`}>{inv.payment_status}</span>} />
        </div>

        {cleared ? (
          <div className="mt-6 flex items-center gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 px-4 py-3 text-sm" data-testid="payment-cleared">
            <CheckCircle2 className="w-5 h-5" /> {inv.payment_status === "waived" ? "The fee for this article has been waived. No payment is required." : "Payment received — thank you. Your article will proceed to proof-reading."}
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              To complete your Article Processing Charge of <strong>{inv.fee}</strong>, please contact our billing office to receive a secure invoice and payment instructions. Quote your manuscript ID <strong>{inv.submission_id}</strong>.
            </p>
            <Button asChild size="lg" className="rounded-full gap-2" data-testid="pay-now-btn">
              <a href={`mailto:${billing}?subject=APC%20Payment%20-%20${encodeURIComponent(inv.submission_id || "")}&body=${encodeURIComponent(`I would like to pay the Article Processing Charge (${inv.fee}) for manuscript "${inv.title}" (${inv.submission_id}).`)}`}>
                <Mail className="w-4 h-4" /> Request payment invoice
              </a>
            </Button>
            <p className="text-xs text-slate-400">Billing office: {billing}</p>
          </div>
        )}
      </div>
    </div>
  );
}
